/**
 * API Service for authentication
 * Handles all API calls related to login and OTP verification
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Request OTP
export const requestOTP = async (phoneNumber) => {
  try {
    const url = `${API_BASE_URL}/api/login`;
    console.log('Calling API:', url);
    console.log('Request payload:', { mobile_number: phoneNumber });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        mobile_number: phoneNumber,
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Response data:', data);
    } else {
      const text = await response.text();
      console.log('Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON. The API endpoint may be incorrect.');
    }

    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        `Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Request OTP Error:', error);
    throw new Error(
      error.message || 'Failed to request OTP. Please try again later.'
    );
  }
};

// Verify OTP
export const verifyOTP = async (phoneNumber, otp) => {
  try {
    const url = `${API_BASE_URL}/api/verify-otp`;
    console.log('Calling Verify OTP API:', url);
    console.log('Request payload:', { mobile_number: phoneNumber, otp });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        mobile_number: phoneNumber,
        otp,
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Response data:', data);
    } else {
      const text = await response.text();
      console.log('Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON.');
    }

    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        `Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Verify OTP Error:', error);
    throw new Error(
      error.message || 'Failed to verify OTP. Please try again later.'
    );
  }
};

// Resend OTP
export const resendOTP = async (phoneNumber) => {
  try {
    const url = `${API_BASE_URL}/api/login`;
    console.log('Calling Resend OTP API:', url);
    console.log('Request payload:', { mobile_number: phoneNumber });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        mobile_number: phoneNumber,
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Response data:', data);
    } else {
      const text = await response.text();
      console.log('Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON.');
    }

    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        `Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Resend OTP Error:', error);
    throw new Error(
      error.message || 'Failed to resend OTP. Please try again later.'
    );
  }
};

// Signup
export const signup = async (userData) => {
  try {
    const url = `${API_BASE_URL}/api/signup`;
    console.log('Calling Signup API:', url);
    console.log('Request payload:', userData);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        first_name: userData.first_name,
        last_name: userData.last_name,
        mobile_number: userData.mobile_number,
        role: userData.role,
        ...(userData.license_no && { license_no: userData.license_no }),
        ...(userData.consultation_fee && { consultation_fee: userData.consultation_fee }),
        ...(userData.lawyer_practice && { lawyer_practice: userData.lawyer_practice }),
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Response data:', data);
    } else {
      const text = await response.text();
      console.log('Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON.');
    }

    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        `Error: ${response.status} ${response.statusText}`;
      
      // Check if the error is because mobile number already exists
      if (response.status === 409 || data.code === 'MOBILE_EXISTS' || data.message?.includes('exist')) {
        const error = new Error(errorMessage);
        error.isMobileExists = true;
        throw error;
      }
      
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Signup Error:', error);
    throw error;
  }
};

// Logout
export const logout = async (phoneNumber) => {
  try {
    const url = `${API_BASE_URL}/api/logout`;
    console.log('Calling Logout API:', url);
    console.log('Request payload:', { mobile_number: phoneNumber });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        mobile_number: phoneNumber,
      }),
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Response data:', data);
    } else {
      const text = await response.text();
      console.log('Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON.');
    }

    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        `Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Logout Error:', error);
    throw new Error(
      error.message || 'Failed to logout. Please try again later.'
    );
  }
};

// Get Profile with Token
export const getProfile = async (token) => {
  try {
    const url = `${API_BASE_URL}/api/getprofile`;
    console.log('Calling Get Profile API:', url);
    console.log('Request headers with token');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers.get('content-type'));

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('Profile data:', data);
    } else {
      const text = await response.text();
      console.log('Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON.');
    }

    if (!response.ok) {
      const errorMessage =
        data.message ||
        data.error ||
        `Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Get Profile Error:', error);
    throw new Error(
      error.message || 'Failed to fetch profile. Please try again later.'
    );
  }
};

// Get Lawyers
export const getLawyers = async (token) => {
  try {
    const url = `${API_BASE_URL}/api/guests/getLawyers`;
    console.log('📋 Calling Get Lawyers API:', url);
    console.log('📋 Using token:', token ? '✅ Token provided' : '❌ No token');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    console.log('📋 Response status:', response.status);

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('📋 Response data:', data);
    } else {
      const text = await response.text();
      console.log('📋 Non-JSON response:', text.substring(0, 200));
      throw new Error(`Expected JSON but got ${contentType || 'unknown content type'}`);
    }

    if (!response.ok) {
      const errorMessage =
        data?.message ||
        data?.error ||
        data?.errors?.message ||
        `Server error: ${response.status} ${response.statusText}`;
      console.error('📋 API Error:', errorMessage);
      throw new Error(errorMessage);
    }

    console.log('📋 ✅ Successfully fetched lawyers');
    return data;
  } catch (error) {
    console.error('❌ Get Lawyers Error:', error.message);
    throw error;
  }
};

// Create Case
export const createCase = async (caseData, token) => {
  try {
    const url = `${API_BASE_URL}/api/guests/add`;
    console.log('📋 Calling Create Case API:', url);
    console.log('📋 Using token:', token ? '✅ Token provided' : '❌ No token');
    console.log('📋 Request payload:', caseData);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify({
        title: caseData.title,
        case_type: caseData.case_type,
        description: caseData.description,
        case_location: caseData.case_location,
        lawyer_id: caseData.lawyer_id,
      }),
    });

    console.log('📋 Response status:', response.status);

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('📋 Response data:', data);
    } else {
      const text = await response.text();
      console.log('📋 Non-JSON response:', text.substring(0, 200));
      throw new Error('Server did not return JSON.');
    }

    if (!response.ok) {
      const errorMessage =
        data?.message ||
        data?.error ||
        `Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    console.log('📋 ✅ Case created successfully');
    return data;
  } catch (error) {
    console.error('❌ Create Case Error:', error);
    throw error;
  }
};

// Get Guest Requests/Cases
export const getGuestRequests = async (token) => {
  try {
    const url = `${API_BASE_URL}/api/guests/by-date`;
    console.log('📋 Calling Get Guest Requests API:', url);
    console.log('📋 Using token:', token ? '✅ Token provided' : '❌ No token');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    console.log('📋 Response status:', response.status);

    const contentType = response.headers.get('content-type');
    let data;

    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
      console.log('📋 Response data:', data);
    } else {
      const text = await response.text();
      console.log('📋 Non-JSON response:', text.substring(0, 200));
      throw new Error(`Expected JSON but got ${contentType || 'unknown content type'}`);
    }

    if (!response.ok) {
      const errorMessage =
        data?.message ||
        data?.error ||
        data?.errors?.message ||
        `Server error: ${response.status} ${response.statusText}`;
      console.error('📋 API Error:', errorMessage);
      throw new Error(errorMessage);
    }

    console.log('📋 ✅ Successfully fetched guest requests');
    return data;
  } catch (error) {
    console.error('❌ Get Guest Requests Error:', error.message);
    throw error;
  }
};

const authService = {
  requestOTP,
  verifyOTP,
  resendOTP,
  signup,
  logout,
  getProfile,
  getLawyers,
  createCase,
  getGuestRequests,
};

export default authService;
